import {
  FieldGroup,
  FGMultiSuggestInput,
  FGTextInput,
  FGEmpty,
  FGTextAreaInput,
  FGCustomPanel,
  FGNumberInput,
  useFGFormik,
  FGMaskInput,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { FGDogSearchInput, SmallFormGenerator } from "../../../../components";
import { useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useTl, useApiService, useCrudApi, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { CoupleInbreedingApi, CoupleInbreedingDto, DogApi, EDogSex } from "../../../../api";
import * as Yup from "yup";
import { Icon } from "@blueprintjs/core";
import { FilePond } from "react-filepond";

export interface ICoupleInbreedingPanelProps {
  dogId: number;
}

export const CoupleInbreedingPanel: React.FunctionComponent<ICoupleInbreedingPanelProps> = ({ dogId }) => {
  const { t } = useTl();
  const api = useApiService(CoupleInbreedingApi);
  const dogApi = useApiService(DogApi);
  const history = useHistory();
  const { languages } = useGlobalData();
  const { dispatchEvent } = useEventsContext();
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      fatherId: Yup.number().nullable().required(t(ETLCodes.Required)),
      motherId: Yup.number().nullable().required(t(ETLCodes.Required)),
      inbreedingPercentage: Yup.number()
        .nullable()
        .min(0, t(ETLCodes.Between0And100))
        .max(100, t(ETLCodes.Between0And100)),
    });
  }, [api, t]);

  const [selectedDogSex, setSelectedDogSex] = React.useState<EDogSex>();

  const { data, loading, deleteItem, deleting, saveItem, saving, refresh } = useCrudApi(
    {
      getApiFn: async (id = dogId) => {
        var dogSex = await dogApi.getDogSex(dogId);
        setSelectedDogSex(dogSex);
        return new CoupleInbreedingDto({
          fatherId: dogSex === EDogSex.Male ? dogId : undefined,
          motherId: dogSex === EDogSex.Female ? dogId : undefined,
        }).toJSON();
      },
      saveApiFn: async (d: CoupleInbreedingDto) => {
        let file = undefined;
        if (d["inbreedingReport"]) {
          file = {
            data: d["inbreedingReport"].file,
            fileName: d["inbreedingReport"].filename,
          };
        }
        const res = await api.save(
          d.coupleInbreedingId ?? 0,
          d.fatherId,
          d.motherId,
          d.inbreedingPercentage,
          file,
          true
        );
        dispatchEvent("RefreshSearchTables");
        return res;
      },
      onSavedRoute: (d) => `${ERoutes.dog}/${dogId}/coupleInbreeding/edit`,
    },
    []
  );

  const { openNewTab } = useWindow();
  const formik = useFGFormik({
    initialValues: data,
    onSubmit: saveItem,
    validationSchema: FormSchema,
  });

  return (
    <SmallFormGenerator
      formik={formik}
      initialValues={data}
      onSubmit={saveItem}
      editMode={true}
      validationSchema={FormSchema}
      loading={loading}
      onDelete={deleteItem}
      showDeleteButton={dogId > 0}
      saving={saving}
      deleting={deleting}
      enableDirtyCheck={false}
    >
      <FieldGroup
        fieldsetProps={{
          title: t(ETLCodes.CreateInbreeding),
        }}
      >
        <FGMaskInput
          cleaveOptions={{
            numeralThousandsGroupStyle: "thousand",
            numeralDecimalMark: ".",
            delimiter: ",",
          }}
          name="inbreedingPercentage"
          label={t(ETLCodes.Inbreeding)}
          rightElement={<Icon icon={"percentage"} />}
        />
        <FGDogSearchInput
          name="fatherId"
          defaultSearch={{ sex: EDogSex.Male }}
          label={t(ETLCodes.Father)}
          onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
          requiredMark
          readonly={selectedDogSex === EDogSex.Male}
        />
        <FGDogSearchInput
          name="motherId"
          defaultSearch={{ sex: EDogSex.Female }}
          label={t(ETLCodes.Mother)}
          onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
          requiredMark
          readonly={selectedDogSex === EDogSex.Female}
        />
        <FilePond
          labelIdle={t(ETLCodes.FilePondDragOrSelect)}
          onupdatefiles={(f) => {
            formik.setFieldValue("inbreedingReport", f.length > 0 ? f[0] : null);
          }}
        />
      </FieldGroup>
    </SmallFormGenerator>
  );
};
